import { cn } from '@/lib/cn';
import { Datum } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { useTooltip } from '@nivo/tooltip';
import useSize from '@react-hook/size';
import { FoundColumn } from '@squaredup/data-streams';
import { clamp } from 'lodash';
import React, { RefObject, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';
import { useDrilldownContext } from 'ui/editor/dataStream/contexts/DrilldownContext';
import GraphTooltip from '../../../components/GraphTooltip';
import { opacityGradient } from '../../util/nivoGradient';
import nivoTheme from '../../util/nivoTheme';
import { DataStreamDonutConfig } from './Config';
import { MAX_LABEL_LENGTH, calculateSize, getScale } from './DataStreamDonutSizing';
import DonutCenterValue from './DonutCenterValue';
import { Donut, convertToRgba, formatDonutTooltipPoints } from './dataUtils';

interface DataStreamDonutVizProps {
    series: Donut[];
    config: DataStreamDonutConfig;
    isAutoVertical: boolean;
    currentlyHoveredId?: number;
    containerRef: RefObject<HTMLElement>;
    valueColumn: FoundColumn;
    centerValue: number;
    totalValue: number;
    getDonutColor: (datum: Datum) => string;
    formatValue: (v: unknown) => string;
    formatPercentage: (v: unknown) => string;
    onHover: (attributes: Datum) => void;
    onLeave: () => void;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export const DataStreamDonutViz: React.FC<DataStreamDonutVizProps> = ({
    series,
    config,
    isAutoVertical,
    currentlyHoveredId,
    containerRef,
    valueColumn,
    centerValue,
    totalValue,
    getDonutColor,
    formatValue,
    formatPercentage,
    onHover,
    onLeave,
    onClick
}) => {
    const donutRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { isDrilldownEnabled } = useDrilldownContext();

    /**
     * Nivo doesn't recreate the tooltip function when valueColumn changes
     * so we need to use a ref to keep it up to date, otherwise the tooltip
     * shows value formatted using the wrong value column.
     */
    const valueColumnRef = useRef(valueColumn);
    valueColumnRef.current = valueColumn;

    const [width, height] = useSize(donutRef);
    const showValuesAsPercentage = useRef<boolean | undefined>(config.showValuesAsPercentage);

    useEffect(() => {
        /**
         * This is a bit of hack as nivo/pie Tooltip prop function
         * does not have access to the current React value of showValuesAsPercentage, unlike the arcLinkLabel prop, etc
         * so if a user switches modes in edit mode, the tooltip will still show the old value
         */
        showValuesAsPercentage.current = config.showValuesAsPercentage;
    }, [config.showValuesAsPercentage]);

    const isHovered = currentlyHoveredId != null;
    const position = config.legendPosition;

    const isVertical = isAutoVertical || position === 'top' || position === 'bottom';
    const isLegendTable = config.legendMode === 'table';

    const scale = getScale(width, height);

    const { size, margins, showLabels } = calculateSize(width, height, scale, isLegendTable, series);

    return (
        <div
            ref={donutRef}
            className={cn('relative w-full h-full overflow-hidden font-inter', onClick && 'cursor-pointer')}
            data-visualization='data-stream-donut-chart'
            onClick={onClick}
        >
            <div
                className={cn('relative', {
                    'absolute inset-0 left-auto m-auto': isLegendTable && !isVertical,
                    'right-auto': position === 'left',
                    'left-auto': position === 'right'
                })}
                style={
                    !isVertical && isLegendTable && donutRef.current
                        ? {
                              width: Math.min(width, height),
                              height: Math.min(width, height)
                          }
                        : { width: '100%', height: '100%' }
                }
            >
                <ResponsivePie
                    data={series}
                    startAngle={0}
                    endAngle={360}
                    margin={margins}
                    innerRadius={0.85}
                    padAngle={1}
                    borderWidth={4} // add transparent border to increase hover area, and reduce flicking as moving around donut
                    borderColor={'transparent'}
                    valueFormat={formatValue}
                    colors={getDonutColor}
                    defs={[opacityGradient('pie-gradient')]}
                    fill={[
                        {
                            match: (d) => isHovered && d.id !== currentlyHoveredId,
                            id: 'pie-gradient'
                        }
                    ]}
                    arcLinkLabelsSkipAngle={clamp(12 / scale, 9, 15)} // Hide labels for small segments
                    theme={{
                        ...nivoTheme,
                        labels: {
                            text: { fontSize: 14, fontWeight: 700, fontFamily: 'montserrat' }
                        }
                    }}
                    tooltip={({ datum }) => {
                        return (
                            <GraphTooltip
                                points={formatDonutTooltipPoints(
                                    datum,
                                    formatValue,
                                    formatPercentage,
                                    valueColumnRef.current.dataIndex,
                                    showValuesAsPercentage.current
                                )}
                                graphRef={containerRef}
                            />
                        );
                    }}
                    enableArcLabels={false}
                    enableArcLinkLabels={showLabels}
                    arcLinkLabel={(datum) => {
                        const id = datum.id;
                        let label = datum.label.toString();
                        const value = config.showValuesAsPercentage
                            ? formatPercentage(datum.data.percentage)
                            : formatValue(datum.value);

                        if (label.length > MAX_LABEL_LENGTH) {
                            label = label.substring(0, MAX_LABEL_LENGTH) + '...';
                        }

                        const fontSize = clamp(10 * scale, 10, 14);
                        const isLabelHovered = id === currentlyHoveredId;

                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const { showTooltipFromEvent, hideTooltip } = useTooltip();

                        const arcLinkLabel = (
                            <tspan
                                className={cn('font-semibold', {
                                    'opacity-20': isHovered && !isLabelHovered
                                })}
                                onMouseEnter={(e) => {
                                    showTooltipFromEvent(
                                        <GraphTooltip
                                            points={formatDonutTooltipPoints(
                                                datum,
                                                formatValue,
                                                formatPercentage,
                                                valueColumnRef.current.dataIndex,
                                                showValuesAsPercentage.current
                                            )}
                                            graphRef={containerRef}
                                        />,
                                        e
                                    );
                                    onHover(datum);
                                }}
                                onMouseLeave={() => {
                                    hideTooltip();
                                    onLeave();
                                }}
                            >
                                <tspan
                                    x='0'
                                    y='-0.5em'
                                    fontSize={fontSize}
                                    className='fill-textPrimary'
                                    data-legendlabel='legendLabel'
                                >
                                    {label}
                                </tspan>
                                <tspan
                                    x='0'
                                    dy='1.2em'
                                    fontSize={fontSize}
                                    className='fill-textSecondary'
                                    data-legendvalue='legendValue'
                                >
                                    {value}
                                </tspan>
                            </tspan>
                        );

                        if (isDrilldownEnabled && datum.data.link) {
                            return (<NavLink to={datum.data.link}>{arcLinkLabel}</NavLink>) as unknown as string;
                        }

                        return arcLinkLabel as unknown as string;
                    }}
                    arcLinkLabelsTextOffset={6}
                    arcLinkLabelsDiagonalLength={clamp(20 * scale, 10, 30)}
                    arcLinkLabelsStraightLength={7}
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={(attributes) => {
                        const id = attributes.id;
                        const isLabelHovered = id === currentlyHoveredId;

                        if (isHovered && !isLabelHovered) {
                            return convertToRgba(attributes.color, 0.2);
                        }

                        return attributes.color;
                    }}
                    arcLabelsTextColor='var(--textSecondary)'
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                    onClick={(datum, e) => {
                        /**
                         * Prevent fullscreen click handler triggering.
                         * Ideally we'd render a link instead on the arc.
                         * But can't currently wrap the arc component.
                         */
                        if (isDrilldownEnabled && datum.data.link) {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(datum.data.link);
                        }
                    }}
                />

                <div className='absolute top-0 left-0 w-full h-full pointer-events-none'>
                    <DonutCenterValue
                        className='absolute inset-0 flex justify-center w-full h-full m-auto pointer-events-none place-items-center text-textPrimary'
                        style={{
                            // 57% is a rough approximate of the inner size of the donut
                            width: size * 0.57,
                            height: size * 0.57
                        }}
                        value={currentlyHoveredId !== undefined ? centerValue : totalValue}
                        formatValue={formatValue}
                        totalValue={totalValue}
                        showPercentage={config.showValuesAsPercentage && currentlyHoveredId !== undefined}
                        formatPercentage={formatPercentage}
                        showValuesAsPercentage={config.showValuesAsPercentage}
                    />
                </div>
            </div>
        </div>
    );
};
